import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import type { UseNavigateResult } from '@tanstack/react-router';

import { useMe } from '@/api/me';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { useCreateEstate } from '@/features/estates/api/estates';
import { EstateForm } from '@/features/estates/components/estate-form';
import { type EstateSchemaType, getCreateEstateSchema } from '@/features/estates/schemas/estate';
import { useEstate } from '@/features/estates-property/api/estate';

import type { useModal } from './new/use-modal';
import { EstateFormDialog } from './estate-form-dialog';

export interface CopyProjectObjectModalProps {
  estateId: string;
  navigate: UseNavigateResult<string>;
  onSuccess?: (estateId: string) => void;
  onSubmit?: () => void;
  modal: ReturnType<typeof useModal>;
}

export const CopyProjectObjectModal: React.FC<CopyProjectObjectModalProps> = ({
  estateId,
  navigate,
  onSuccess,
  onSubmit: onSubmitProp,
  modal,
}) => {
  const { t } = useTranslation();
  const { data: estate } = useEstate({ id: estateId });
  const schema = getCreateEstateSchema();
  const { data: me } = useMe();
  const queryClient = useQueryClient();

  const { mutateAsync: createEstateAsync } = useCreateEstate({
    mutationConfig: {
      onSuccess: ([createdEstate]) => {
        if (onSuccess) {
          onSuccess(createdEstate.id);
        } else {
          navigate({ to: '/estates/$estateId', params: { estateId: createdEstate.id } });
        }
        // Invalidate estates queries so the estates table refreshes
        queryClient.invalidateQueries({ queryKey: ['estates'] });
        navigate({ to: '/estates/$estateId', params: { estateId: createdEstate.id } });
        toast.success(t('create-estate-success'));
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const methods = useForm<EstateSchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      estateData: {
        name: estate?.name || '',
        propertyType: estate?.estateDetails?.propertyType || '',
        marketType: estate?.estateDetails?.marketType || '',
        tenantId: estate?.tenantId || '',
        projectId: estate?.projectId || '',
        creatorId: me?.id,
        propertyLevel: PROPERTY_LEVELS.OBJECT,
      },
      addressData: {
        ...(estate?.address?.address && {
          addressAutoComplete: estate.address.address,
          address: estate.address.address,
        }),
        postalCode: estate?.address?.postalCode || '',
        city: estate?.address?.city || '',
        ...(estate?.address?.street && { street: estate.address.street }),
        ...(estate?.address?.houseNumber && {
          houseNumber: estate.address.houseNumber,
        }),
        countryId: estate.address?.country?.id || '',
      },
    },
  });

  const handleSubmit = async (args: EstateSchemaType) => {
    onSubmitProp?.();
    await createEstateAsync(args);
  };

  return (
    <EstateFormDialog modal={modal} title={t('add-new-estate')}>
      <FormProvider {...methods}>
        <EstateForm
          closeModal={modal.dismiss}
          onCancel={modal.dismiss}
          label={t('add-new-estate')}
          onSubmit={handleSubmit}
          options={{
            newUi: true,
            projectDisabled: !!estate?.projectId,
            propertyLevelDisabled: true,
            tenantDisabled: !!estate?.tenantId,
          }}
        />
      </FormProvider>
    </EstateFormDialog>
  );
};
